export type DesktopOs = "mac" | "windows";

/** One build per OS: macOS ships a single universal .dmg. */
export type DesktopBuildId = DesktopOs;

export interface DesktopBuild {
  id: DesktopBuildId;
  os: DesktopOs;
  /** Label used on the download button, e.g. "Mac". */
  label: string;
  /** Second line on the platform buttons, e.g. "64-bit · .exe". */
  hint: string;
  fileName: string;
  url: string;
  /** Known size, when the release publishes one. Otherwise Content-Length. */
  sizeBytes?: number;
}

export interface DesktopRelease {
  version: string | null;
  builds: DesktopBuild[];
  /**
   * False when the release could not be read, so the file names are unknown and
   * the download buttons would point at nothing.
   */
  available: boolean;
}

export const DESKTOP_SETUP_GUIDE_URL =
  process.env.NEXT_PUBLIC_DESKTOP_SETUP_GUIDE_URL ?? null;

export const SUPPORT_EMAIL = "support@yolk.coach";

/** Single source of truth for the requirements line, shown in several places. */
export const SYSTEM_REQUIREMENTS = "Windows 10+ and macOS 14.4+";

/**
 * Dev-only escape hatch: /app?src=<url> downloads that file instead of the
 * released build, so the progress experience can be tried against any host.
 * Ignored in production builds.
 */
export const resolveBuildUrl = (build: DesktopBuild) => {
  if (process.env.NODE_ENV === "production" || typeof window === "undefined") {
    return build.url;
  }
  return new URLSearchParams(window.location.search).get("src") ?? build.url;
};

/**
 * Hands the URL straight to the browser's own download manager. No fetch, so
 * no CORS involved — useful as a fallback and as a way to check the bucket
 * without the in-page progress experience (/app?direct=1).
 */
export const downloadInBrowser = (build: DesktopBuild) => {
  const link = document.createElement("a");
  link.href = resolveBuildUrl(build);
  // Ignored for cross-origin URLs, but harmless and correct on same-origin.
  link.download = build.fileName;
  // Cross-origin means the browser navigates rather than downloads when the
  // URL is wrong; a new tab keeps that error off the landing page.
  link.target = "_blank";
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const getPlatformString = () =>
  (navigator as Navigator & { userAgentData?: { platform?: string } })
    .userAgentData?.platform ?? navigator.userAgent;

/** iPadOS 13+ reports a desktop Safari UA, so touch points give it away. */
const isIpadOs = () =>
  navigator.maxTouchPoints > 1 && /macintel|mac os/i.test(getPlatformString());

/** Phones and tablets cannot run the installer. */
export const isDesktopDevice = () => {
  if (typeof navigator === "undefined") return true;
  return (
    !/android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) && !isIpadOs()
  );
};

/**
 * null means "a desktop we do not ship a build for" — Linux, ChromeOS and
 * anything unrecognised. Those visitors get the platform list instead of a
 * button offering them the wrong installer.
 */
export const detectBuildId = (): DesktopBuildId | null => {
  if (typeof navigator === "undefined") return null;
  const platform = getPlatformString();
  if (/win/i.test(platform)) return "windows";
  if (/mac/i.test(platform)) return "mac";
  return null;
};

export const formatBytes = (bytes: number) => {
  const mb = bytes / 1024 / 1024;
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
};

/** "45.1 of 128.0 MB" — the pair shown while bytes are arriving. */
export const formatProgressBytes = (received: number, total: number) =>
  `${(received / 1024 / 1024).toFixed(1)} of ${formatBytes(total)}`;

export const formatEta = (seconds: number) => {
  if (seconds < 1) return "less than a second left";
  if (seconds < 60) return `about ${Math.round(seconds)}s left`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `about ${minutes} min left`;
  return `about ${Math.round(minutes / 60)} hr left`;
};
